// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: playback of soundboard sounds (strand AG). It is the entrance
// sound engine's twin (features/voice/engine/EntranceSoundPlaybackEngine)
// and deliberately takes the same route to the speakers: one AudioContext
// with the user's output device (setSinkId), the user's output volume and
// the master volume, straight to ctx.destination like every other
// call-related sound. Only the gating differs: a soundboard sound is not a
// rejoin chime, but it does carry a per-sound volume set in the guild.
//
// Being a separate engine keeps a burst of soundboard sounds from evicting
// entrance sounds out of that engine's small buffer cache.

import SoundboardVolume from '@app/parley/soundboard/SoundboardVolume';
import {Logger} from '@app/features/platform/utils/AppLogger';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import Sound from '@app/features/ui/state/Sound';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {
	getVoiceEngineV2SnapshotFromMediaEngine,
	setVoiceEngineV2ParticipantAudioLevelSpeaking,
} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import {selectVoiceEngineV2AppParticipants} from '@app/features/voice/engine/v2/VoiceEngineV2AppSelectors';
import EntranceSoundListenerPrefs from '@app/features/voice/state/EntranceSoundListenerPrefs';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {observable, runInAction} from 'mobx';

const logger = new Logger('SoundboardPlaybackEngine');

const BUFFER_CACHE_LIMIT = 24;
const SPEAKING_RELEASE_PADDING_MS = 80;
const IDENTITY_RE = /^user_(\d+)(?:_(.+))?$/;
const MAX_MASTER_VOLUME_PERCENT = 200;
const MAX_GAIN = 3;

/** A sound that is sounding right now, for the panel's progress sweep. */
export interface SoundboardPlaying {
	/** Bumped on every start, so a replay restarts the sweep. */
	startedAt: number;
	durationMs: number;
	/** How many plays of this sound overlap. */
	count: number;
}

export interface SoundboardPlayParams {
	userId: string;
	hash: string;
	url: string;
	durationMs: number;
	/** The sound's own volume (0..1), set by whoever manages the guild. */
	volume: number;
}

class SoundboardPlaybackEngine {
	private audioContext: AudioContext | null = null;
	private bufferCache = new Map<string, AudioBuffer>();
	private speakingTimers = new Map<string, NodeJS.Timeout>();
	/** parley: what is sounding right now, so the panel can silence it. */
	private playingSources = new Map<AudioBufferSourceNode, string>();
	private lastAppliedSinkId: string | null = null;
	/** Set once a sound has really produced samples; the browser test reads it. */
	lastPlayedAt = 0;
	/** parley: what is sounding, keyed by the sound's hash (observable). */
	readonly playing = observable.map<string, SoundboardPlaying>();

	isPlaying(hash: string): boolean {
		return this.playing.has(hash);
	}

	private markPlaying(hash: string, durationMs: number): void {
		runInAction(() => {
			const current = this.playing.get(hash);
			this.playing.set(hash, {startedAt: Date.now(), durationMs, count: (current?.count ?? 0) + 1});
		});
	}

	private markStopped(hash: string): void {
		runInAction(() => {
			const current = this.playing.get(hash);
			if (!current) return;
			if (current.count <= 1) this.playing.delete(hash);
			else this.playing.set(hash, {...current, count: current.count - 1});
		});
	}

	private ensureContext(): AudioContext | null {
		if (typeof window === 'undefined') return null;
		if (this.audioContext && this.audioContext.state !== 'closed') {
			if (this.audioContext.state === 'suspended') {
				void this.audioContext.resume().catch((error) => {
					logger.debug('Soundboard AudioContext resume rejected', {error});
				});
			}
			return this.audioContext;
		}
		const Ctor =
			window.AudioContext || (window as typeof window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
		if (!Ctor) return null;
		try {
			this.audioContext = new Ctor({latencyHint: 'interactive'});
			this.lastAppliedSinkId = null;
			return this.audioContext;
		} catch (error) {
			logger.warn('Failed to create AudioContext', {error});
			return null;
		}
	}

	private async fetchAndDecode(url: string, hash: string): Promise<AudioBuffer | null> {
		const cached = this.bufferCache.get(hash);
		if (cached) {
			// Touch it: the cache is a small LRU, like the entrance sounds'.
			this.bufferCache.delete(hash);
			this.bufferCache.set(hash, cached);
			return cached;
		}
		const ctx = this.ensureContext();
		if (!ctx) return null;
		try {
			const response = await fetch(url, {cache: 'force-cache'});
			if (!response.ok) {
				logger.warn('Soundboard fetch failed', {url, status: response.status});
				return null;
			}
			const buffer = await ctx.decodeAudioData(await response.arrayBuffer());
			this.bufferCache.set(hash, buffer);
			while (this.bufferCache.size > BUFFER_CACHE_LIMIT) {
				const oldest = this.bufferCache.keys().next().value;
				if (!oldest) break;
				this.bufferCache.delete(oldest);
			}
			return buffer;
		} catch (error) {
			logger.warn('Soundboard decode failed', {url, error});
			return null;
		}
	}

	private findParticipantIdentities(userId: string): Array<string> {
		const snapshot = getVoiceEngineV2SnapshotFromMediaEngine();
		if (!snapshot) return [];
		const identities: Array<string> = [];
		for (const {identity} of selectVoiceEngineV2AppParticipants(snapshot).participants) {
			const match = IDENTITY_RE.exec(identity);
			if (match && match[1] === userId) identities.push(identity);
		}
		return identities;
	}

	private setSpeakingFor(userId: string, speaking: boolean): void {
		for (const identity of this.findParticipantIdentities(userId)) {
			setVoiceEngineV2ParticipantAudioLevelSpeaking(identity, speaking);
		}
	}

	private scheduleSpeakingRelease(userId: string, durationMs: number): void {
		const existing = this.speakingTimers.get(userId);
		if (existing) clearTimeout(existing);
		const timer = setTimeout(() => {
			this.speakingTimers.delete(userId);
			this.setSpeakingFor(userId, false);
		}, Math.max(0, durationMs) + SPEAKING_RELEASE_PADDING_MS);
		this.speakingTimers.set(userId, timer);
	}

	private applyOutputDevice(ctx: AudioContext): void {
		const deviceId = VoiceSettings.getOutputDeviceId();
		const sinkId = !deviceId || deviceId === 'default' ? '' : deviceId;
		if (sinkId === this.lastAppliedSinkId) return;
		if (sinkId === '' && this.lastAppliedSinkId === null) return;
		const sinkable = ctx as AudioContext & {setSinkId?: (sinkId: string) => Promise<void>};
		if (typeof sinkable.setSinkId !== 'function') return;
		const previous = this.lastAppliedSinkId;
		this.lastAppliedSinkId = sinkId;
		void sinkable.setSinkId(sinkId).catch((error) => {
			this.lastAppliedSinkId = previous;
			logger.debug('Failed to apply the output device to the soundboard context', {sinkId, error});
		});
	}

	/** The gain a sound gets: its own volume times the listener's settings. */
	gainFor(userId: string, soundVolume: number): number {
		const listenerPct = EntranceSoundListenerPrefs.getVolume(userId);
		const outputPct = VoiceSettings.getOutputVolume();
		const masterPct = Math.max(0, Math.min(MAX_MASTER_VOLUME_PERCENT, Sound.getMasterVolume()));
		const sound = Math.max(0, Math.min(1, soundVolume));
		// The listener's own soundboard volume, from the speaker button in
		// the panel (SoundboardVolume.ts).
		const boardGain = SoundboardVolume.gain;
		return Math.max(
			0,
			Math.min(MAX_GAIN, sound * boardGain * (listenerPct / 100) * (outputPct / 100) * (masterPct / 100)),
		);
	}

	async play(params: SoundboardPlayParams): Promise<void> {
		const {userId, hash, url, durationMs, volume} = params;
		// Same gates as an entrance sound: a deafened or muted-for-me user
		// hears nothing, streamer mode silences everything, and the global
		// sound switch wins over all of it.
		if (EntranceSoundListenerPrefs.isMuted(userId)) return;
		if (getEffectiveAudioState().effectiveDeaf) return;
		if (StreamerMode.shouldDisableSounds) return;
		if (!Sound.getSoundEnabled()) return;
		const ctx = this.ensureContext();
		if (!ctx) return;
		this.applyOutputDevice(ctx);
		const buffer = await this.fetchAndDecode(url, hash);
		if (!buffer) return;
		try {
			const source = ctx.createBufferSource();
			source.buffer = buffer;
			const gain = ctx.createGain();
			gain.gain.value = this.gainFor(userId, volume);
			source.connect(gain).connect(ctx.destination);
			this.setSpeakingFor(userId, true);
			this.scheduleSpeakingRelease(userId, durationMs || buffer.duration * 1000);
			this.playingSources.set(source, hash);
			source.onended = () => {
				this.playingSources.delete(source);
				this.markStopped(hash);
				const timer = this.speakingTimers.get(userId);
				if (timer) {
					clearTimeout(timer);
					this.speakingTimers.delete(userId);
				}
				this.setSpeakingFor(userId, false);
			};
			source.start();
			// Only a started source ends, so only a started one is marked.
			this.markPlaying(hash, durationMs || buffer.duration * 1000);
			this.lastPlayedAt = Date.now();
		} catch (error) {
			logger.warn('Failed to play a soundboard sound', {userId, hash, error});
			this.setSpeakingFor(userId, false);
		}
	}

	/**
	 * parley (strand AJ): the stop button in the panel. It only silences
	 * *our* speakers - a sound someone else triggered keeps playing for
	 * everyone else, because nobody in a channel may mute another person's
	 * playback remotely.
	 */
	stopAll(): void {
		for (const source of [...this.playingSources.keys()]) this.stopSource(source);
	}

	/** Silences one sound for us, e.g. the preview button of the settings list. */
	stop(hash: string): void {
		for (const [source, playingHash] of [...this.playingSources]) {
			if (playingHash === hash) this.stopSource(source);
		}
	}

	private stopSource(source: AudioBufferSourceNode): void {
		try {
			source.stop();
		} catch {
			// Already finished; onended does the bookkeeping.
		}
	}

	clear(): void {
		this.stopAll();
		for (const timer of this.speakingTimers.values()) clearTimeout(timer);
		this.speakingTimers.clear();
	}
}

const instance = new SoundboardPlaybackEngine();

declare global {
	interface Window {
		_soundboardPlaybackEngine?: SoundboardPlaybackEngine;
	}
}

if (typeof window !== 'undefined') {
	window._soundboardPlaybackEngine = instance;
}

export default instance;
